// What a member meets while the company's payout contract waits for a signature
// they may not give (an administrator signs): the stop, as Apple stops a team
// until the account holder accepts an agreement, and the way to write to the
// administrators — a message sent to each of them with the member's name and
// address (vendor admins_message), so they can answer by mail.
import React, { useState } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import Typography from "aldoo-ra/Typography"
import { showNotification } from "aldoo-ra/Notification"
import { DocumentTextIcon } from "@heroicons/react/24/outline"
import { MessageOrgAdmins } from "../AdminShared/api"
import { errorMessage } from "../AdminShared/ui"
import usePortalTexts from "./texts/usePortalTexts"
import { PORTAL } from "./texts/bundles"

export default function ContractPendingNotice({ orgId }) {
  const t = usePortalTexts(PORTAL)
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState("")
  const [busy, setBusy] = useState(false)

  const close = () => {
    if (busy) return
    setOpen(false)
  }
  const send = async () => {
    if (message.trim().length < 3) return
    setBusy(true)
    try {
      const res = await MessageOrgAdmins(orgId, message.trim())
      if (res?.error) return showNotification({ message: errorMessage(res.error), className: "bg-admin_error text-white" })
      showNotification({ message: t`Your message to the administrators has been sent`, className: "bg-admin_success text-white" })
      setMessage("")
      setOpen(false)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="rounded-xl border border-admin_bordered bg-admin_paper px-6 py-12 md:py-16 flex flex-col items-center text-center gap-5" data-testid="contract-pending-notice">
      <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center">
        <DocumentTextIcon className="w-8 h-8 text-amber-500" />
      </div>
      <div className="flex flex-col gap-2 max-w-[560px]">
        <Typography variant="h3">{t`Waiting for your administrator's signature`}</Typography>
        <Typography variant="body1_dim" className="mt-0">
          {t`Your company administrator should sign the payout contract before you can use the platform's features.`}{" "}
          {t`You can contact them here.`}
        </Typography>
      </div>
      <Button variant="admin_primary" onClick={() => setOpen(true)} data-testid="contract-pending-contact">
        {t`Contact the administrators`}
      </Button>
      <Modal
        isOpen={open}
        onClose={close}
        variant="admin_compact"
        title={t`Write to the administrators`}
        styleOverrides={{ container: "bg-admin_paper border-admin_bordered w-full max-w-[520px] rounded-md border shadow-lg" }}
      >
        <div className="flex flex-col gap-3 p-4" data-testid="contract-pending-dialog">
          <Typography variant="admin_txt1">
            {t`Every administrator of the company gets your message by email, with your name and address, so they can answer you.`}
          </Typography>
          <Input
            variant="admin"
            multiline
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            rows={5}
            maxLength={2000}
            placeholder={t`Please sign the payout contract so I can start working…`}
            className="text-sm"
            data-testid="contract-pending-message"
          />
          <div className="flex justify-end gap-2">
            <Button variant="admin_secondary" onClick={close} disabled={busy}>
              {t`Cancel`}
            </Button>
            <Button variant="admin_primary" onClick={send} disabled={busy || message.trim().length < 3} data-testid="contract-pending-send">
              {busy ? t`Sending…` : t`Send`}
            </Button>
          </div>
        </div>
      </Modal>
    </section>
  )
}
